import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../store/authStore';
import { LoginFormData, LoginFormErrors } from './types';

export const useLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  // Get redirect path from location state or default to /otc
  const redirectTo =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    '/otc';

  // Redirect only when becoming authenticated (not on initial load)
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  // Clear error when form data changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [formData, clearError, error]);

  const validateForm = useCallback((): boolean => {
    const errors: LoginFormErrors = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData.email, formData.password]);

  const handleInputChange = useCallback(
    (field: keyof LoginFormData, value: string | boolean) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));

      // Clear field error when user types
      if (formErrors[field as keyof LoginFormErrors]) {
        setFormErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [formErrors]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      try {
        await login(formData.email, formData.password, false);
      } catch {
        // Error is already set in authStore and will be displayed via the error prop
      }
    },
    [formData, login, validateForm]
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  return {
    formData,
    formErrors,
    showPassword,
    isLoading,
    error: error?.message || null,
    handleInputChange,
    handleSubmit,
    togglePasswordVisibility,
  };
};
